"use client";

import React from "react";
import styles from "./SignupToEarnBox.module.css";
import { Translate } from "@/components/Translate";
import { Button } from "@/components/ui/Button";
import { useUser } from "@frend-digital/centra/client";
import type { BlokProps } from "@frend-digital/storyblok";
import type { BlokOrderLoginPrompt } from "@/storyblok";
import { Prose } from "@/components/ui/Prose";
import { RichTextRender } from "@/components/ui/RichTextRender";
import { useLoyaltyContent } from "../../use-loyalty-content";
import { useAccountModal } from "@/features/account/AccountModal";

export const OrderLoginPromptBlok = ({
	points,
	blok,
	preview,
}: BlokProps<BlokOrderLoginPrompt> & {
	points: number;
}) => {
	let state: "not-signed-in" | "signed-in" = "not-signed-in";

	const { data: user, isLoading } = useUser();
	const [, setOpen] = useAccountModal();

	if (user) state = "signed-in";

	if (isLoading) return null;

	return (
		<div className={styles.root}>
			{(() => {
				switch (state) {
					case "not-signed-in":
						return (
							<>
								<p>
									<Translate parseHTML params={{ points }}>
										{blok.title || ""}
									</Translate>
								</p>

								<p>
									<Translate parseHTML params={{ points }}>
										{blok.subtitle || ""}
									</Translate>
								</p>

								<div className={styles.buttons}>
									<Button onClick={() => setOpen(true, "register")}>{blok.registerText}</Button>
									<Button color="outlinedTransparent" onClick={() => setOpen(true, "login")}>
										{blok.loginText}
									</Button>
								</div>

								<div>
									{blok.footer && <RichTextRender small blok={blok.footer} preview={preview} />}
								</div>
							</>
						);

					case "signed-in":
						return (
							<>
								{blok.signedInTitle && (
									<p>
										<Translate parseHTML params={{ points }}>
											{blok.signedInTitle}
										</Translate>
									</p>
								)}
								{blok.signedInSubtitle && (
									<p>
										<Translate parseHTML params={{ points }}>
											{blok.signedInSubtitle}
										</Translate>
									</p>
								)}

								{blok.signedInFooter && (
									<RichTextRender
										className={styles.customRichText}
										small
										blok={blok.signedInFooter}
										preview={preview}
									/>
								)}
							</>
						);

					default:
						return null;
				}
			})()}
		</div>
	);
};

export const OrderLoginPrompt = ({ points }: { points: number }) => {
	const { data } = useLoyaltyContent();

	const loginPrompt = data?.content?.signupPrompt?.[0];

	if (!loginPrompt) return null;

	return <OrderLoginPromptBlok points={points} blok={loginPrompt} preview={true} />;
};
